import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CLIP_VARIANT_STATUS_VALUES, type ClipVariantStatus } from '@content-engine/shared';
import { fetchBanners, fetchVariants } from '../../lib/queries.ts';
import {
  ArchivedChip,
  FileChip,
  MediaPlaceholder,
  ParentLink,
  ShowArchivedCheckbox,
  formatBytes,
  isFreed,
} from '../../components/archive/index.ts';
import { variantTitle } from './title.ts';

const PAGE_SIZE = 20;

/**
 * Плоский список вариантов (прототип §6.1). Вариант — конечный продукт: он
 * живёт своей жизнью и публикуется, даже если клип и фильм уже в архиве.
 * Колонка «Клип» показывает состояние родителя честно, но ничего у варианта
 * не отнимает — способности отнимает только освобождение файла.
 */
export function VariantsPage(): React.ReactElement {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<ClipVariantStatus | ''>('');
  const [bannerId, setBannerId] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const variantsQuery = useQuery({
    queryKey: ['variants', page, status, bannerId, showArchived],
    queryFn: () =>
      fetchVariants({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        status: status ? [status] : [],
        ...(bannerId ? { bannerId } : {}),
        showArchived,
      }),
    refetchInterval: (q) => {
      const list = q.state.data?.variants ?? [];
      return list.some((v) => v.status === 'pending' || v.status === 'rendering') ? 3_000 : false;
    },
  });

  const variants = variantsQuery.data?.variants ?? [];
  const total = variantsQuery.data?.total ?? 0;
  const counts = variantsQuery.data?.counts ?? null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const bannersQuery = useQuery({
    queryKey: ['banners', 'all'],
    queryFn: () => fetchBanners({ status: 'all' }),
    staleTime: 60_000,
  });
  const banners = bannersQuery.data?.banners ?? [];

  useEffect(() => {
    if (!variantsQuery.data) return;
    if (page >= totalPages) setPage(totalPages - 1);
  }, [totalPages, page, variantsQuery.data]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('variants.title')}</h1>
          <div className="sub">
            {t('variants.subtitleCounts', {
              active: counts?.active ?? 0,
              archived: counts?.archived ?? 0,
            })}
          </div>
        </div>
      </div>

      <p style={{ margin: '6px 2px 18px', color: 'var(--text-3)', fontSize: 12.5 }}>
        {t('variants.hint')}
      </p>

      <div
        className="filters"
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}
      >
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ClipVariantStatus | '');
            setPage(0);
          }}
        >
          <option value="">{t('variants.filters.allStatuses')}</option>
          {CLIP_VARIANT_STATUS_VALUES.map((s) => (
            <option key={s} value={s}>
              {t(`clips.variantStatus_${s}`)}
            </option>
          ))}
        </select>
        <select
          value={bannerId}
          onChange={(e) => {
            setBannerId(e.target.value);
            setPage(0);
          }}
        >
          <option value="">{t('variants.filters.allBanners')}</option>
          {banners.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <ShowArchivedCheckbox
          checked={showArchived}
          onChange={(v) => {
            setShowArchived(v);
            setPage(0);
          }}
        />
      </div>

      <div className="section">
        {variantsQuery.isLoading && <div style={{ padding: 16 }}>{t('common.loading')}</div>}
        {variants.length === 0 && !variantsQuery.isLoading && (
          <div style={{ padding: 24, color: 'var(--text-3)' }}>{t('variants.noVariants')}</div>
        )}
        {variants.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>{t('variants.table.variant')}</th>
                <th>{t('variants.table.clip')}</th>
                <th>{t('variants.table.banner')}</th>
                <th>{t('variants.table.size')}</th>
                <th>{t('variants.table.file')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {variants.map((v) => (
                <tr key={v.id} className={v.archivedAt ? 'arch' : undefined}>
                  <td>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      {isFreed(v.file) ? (
                        <MediaPlaceholder file={v.file} variant="thumb" />
                      ) : (
                        <div className="thumb" />
                      )}
                      <div>
                        <div style={{ fontWeight: 600 }}>{variantTitle(v)}</div>
                        <div style={{ color: 'var(--text-3)' }}>
                          {t(`clips.variantStatus_${v.status}`)} <ArchivedChip archivedAt={v.archivedAt} />
                          {v.bannerTimingMode === 'freeze' && (
                            <span title={t('variants.detail.freeze')} style={{ marginLeft: 6 }}>
                              ⏸ {Number(v.freezeAtSec)}s +{Number(v.freezeDurationSec)}s
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{v.clip ? <ParentLink parent={v.clip} kind="clip" /> : '—'}</td>
                  <td style={{ color: 'var(--text-3)' }}>{v.banner?.name ?? '—'}</td>
                  <td style={{ color: 'var(--text-3)' }}>
                    {formatBytes(v.sizeBytes ?? v.file.sizeBytes) ?? '—'}
                  </td>
                  <td>
                    <FileChip file={v.file} />
                  </td>
                  <td>
                    <Link to={`/variants/${v.id}`}>
                      <button type="button">{t('common.open')}</button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {total > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              padding: 12,
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← {t('common.prev')}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {t('clips.pagination.rangeOf', {
                from: page * PAGE_SIZE + 1,
                to: Math.min((page + 1) * PAGE_SIZE, total),
                total,
              })}{' '}
              · {t('clips.pagination.pageOf', { current: page + 1, total: totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
            >
              {t('common.next')} →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
